import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { LANGUAGE_CONSTANTS as LANG_CONST } from '../shared/constants/language-constants';
import { BehaviorSubject, Observable, of } from 'rxjs';
import {
  IShareAccess,
  IAVailableUnit,
  UserCards,
  ActiveIssues,
} from '../shared-layout/Components/activity/user-card.interface';
import { map } from 'rxjs/operators';
import { ERROR_CODES } from '../shared/constants/error-codes';
import { StateManagementService } from '../shared/services/state-management.service';
import { CONSTANTS } from '../shared/constants/constants';
import { NotFoundError } from '../shared/errors/errors';
import { IUserProfile } from '../user-management/user-management-interfaces';
import { Utility } from '../shared/utils/utility.service';
import {
  unitStatusResponse,
  userTypeResponse,
} from '../shared/constants/response-mapping';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root',
})
export class DashboardManagementService {
  private url = environment.graphqlUrl;
  readonly LANG_CONST = LANG_CONST;
  readonly maxCardRecords: number = 3;
  public triggerRerender = new BehaviorSubject<boolean>(false);
  /**
   * @description injecting services to the constructor
   * @param { HttpClient } http
   */
  constructor(
    private http: HttpClient,
    private stateManagementService: StateManagementService,
    private translate: TranslateService
  ) {}

  /**
   * @description this method calls API to get all this defaultUserList, availableUnit, maintenance by site Id
   * @param { string } siteId
   * @returns { defaultUserList, availableUnit, maintenance }
   */
  getDashboardStatistics(siteId: string): Observable<UserCards | undefined> {
    return this.http
      .post<any>(`${this.url}`, {
        query: `query ($siteId: String!) {
          getDashboardStatistics(siteId: $siteId) {
            defaultUserGroups {
              userGroupId
              userGroup
              users {
                userImgUrl
                userImgName
                userImgKey
                id
                user
                accessPointName
                unitName
                floorName
                buildingName
                startTime
                expireTime
                repeatStartTime
                repeatEndTime
                repeatDays
                userType
                isExpired
              }
              totalCount
              cursor
            }
            availableUnits {
              units {
                unitId
                unitName
                unitStatus
                floorId
                floorName
                unitPrefix
                buildingId
                buildingName
              }
              totalCount
            }
            recurrentRevenue {
              inviteCount
              invitesAcceptedCount
              invitesRemaining
              totalInvites
            }
            sharedAccesses {
              access {
                unitId
                unitName
                floorName
                buildingName
                startTime
                expireTime
                repeatStartTime
                repeatEndTime
                repeatDays
                email
                unitPrefix
                accessPointId
                accessPointName
                deviceId
                deviceName
                userType
                userCount
                siteId
                buildingId
                floorId
                id
            
              }
              totalCount
            }
            activeIssues {
              issues {
                id
                eventCode
                eventDescription
                user
                timestamp
                deviceName
                unitName
                buildingName
                buildingId
                unitId
                floorId
                isoTime
                accessPointId
                accessPointName
                unitPrefix
              }
              totalCount
            }
          }
        }       
        `,
        variables: {
          siteId: siteId,
        },
      })
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.getDashboardStatistics) {
              let dashboardData: any = res?.data?.getDashboardStatistics;
              // Update unitStatus values in availableUnits array using map
              dashboardData.availableUnits = dashboardData?.availableUnits?.map(
                (unitGroup: any) => {
                  unitGroup.units = unitGroup.units.map((unit: any) => {
                    if (
                      unit &&
                      unitStatusResponse.hasOwnProperty(unit.unitStatus)
                    ) {
                      return {
                        ...unit,
                        unitStatus: this.translate.instant(
                          unitStatusResponse[unit.unitStatus]
                        ),
                      };
                    }
                    return unit;
                  });
                  return unitGroup;
                }
              );

              // Map through defaultUserGroups and update userType values
              dashboardData.defaultUserGroups =
                dashboardData?.defaultUserGroups?.map((userGroup: any) => {
                  userGroup.users = userGroup.users.map((user: any) => {
                    if (
                      user &&
                      userTypeResponse.hasOwnProperty(user.userType)
                    ) {
                      return {
                        ...user,
                        userType: this.translate.instant(
                          userTypeResponse[user.userType]
                        ),
                      };
                    }
                  });
                  userGroup.userGroup = this.translate.instant(
                    userGroup.userGroup
                  );
                  return userGroup;
                });

              // Map through sharedAccesses and update userType values
              dashboardData.sharedAccesses = dashboardData?.sharedAccesses?.map(
                (sharedAccess: any) => {
                  sharedAccess.access = sharedAccess.access.map(
                    (access: any) => {
                      if (
                        access &&
                        userTypeResponse.hasOwnProperty(access.userType)
                      ) {
                        return {
                          ...access,
                          userType: this.translate.instant(
                            userTypeResponse[access.userType]
                          ),
                        };
                      }
                    }
                  );
                  return sharedAccess;
                }
              );

              this.stateManagementService.updateDashboard(dashboardData);
              return dashboardData;
            } else if (res?.errors?.length > 0) {
              this.stateManagementService.updateDashboard(undefined);
              let error = res?.errors[0];
              switch (error.errorType) {
                case ERROR_CODES.INTERNAL_SERVER_ERROR:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_DASHBOARD_DETAILS')
                  );
                default:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_DASHBOARD_DETAILS')
                  );
              }
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }

  /**
   * @description this method calls API to get getSharedAccessBySite by site Id
   * @param { string } siteId
   * @returns { IShareAccess }
   */
  getDashboardSharedAccess(
    siteId: string
  ): Observable<IShareAccess | undefined> {
    return this.http
      .post<any>(`${this.url}`, {
        query: `query  MyQuery($siteId: String!) {
           getSharedAccessBySite(siteId: $siteId) {
           sharedAccesses {
           totalCount
           access {
           accessPointId
           accessPointName
           buildingName
           buildingId
           floorId
           floorName
           unitName
           unitId
           unitPrefix
           userCount
           }
           }
           }
          }`,
        variables: {
          siteId: siteId,
        },
      })
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.getSharedAccessBySite) {
              let sharedAccess: any = res?.data?.getSharedAccessBySite;
              return sharedAccess;
            } else if (res?.errors?.length > 0) {
              let error = res?.errors[0];
              switch (error.errorType) {
                case ERROR_CODES.INTERNAL_SERVER_ERROR:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_DASHBOARD_DETAILS')
                  );
                default:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_DASHBOARD_DETAILS')
                  );
              }
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }

  /**
   * @description this method calls API to get getDashboardAvailableUnits by site Id
   * @param { string } siteId
   * @returns { IAVailableUnit }
   */
  getDashboardAvailableUnits(
    siteId: string,
    cursor: string[],
    hideLoader?: boolean
  ): Observable<IAVailableUnit | undefined> {
    const pagination = {
      size: 10,
      ...(cursor.every((value) => value !== '') && { cursor }),
    };
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query: `
        query MyQuery($pagination: Pagination, $siteId: String!) {
         getAvailableUnitsBySite(siteId: $siteId, pagination: $pagination) {
         availableUnits {
         cursor
         totalCount
         units {
         buildingName
         buildingId
         floorId
         floorName
         unitId
         unitName
         unitStatus
         unitPrefix
         }
         }
         }
        }
        
        `,
          variables: {
            siteId: siteId,
            pagination,
          },
        },
        {
          headers: Utility.loaderHeader(hideLoader),
        }
      )
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.getAvailableUnitsBySite) {
              let availableUnits: any = res?.data?.getAvailableUnitsBySite;

              // Update unitStatus values in availableUnits array using map
              availableUnits.availableUnits = availableUnits.availableUnits.map(
                (unitGroup: any) => {
                  unitGroup.units = unitGroup.units.map((unit: any) => {
                    if (
                      unit &&
                      unitStatusResponse.hasOwnProperty(unit.unitStatus)
                    ) {
                      return {
                        ...unit,
                        unitStatus: this.translate.instant(
                          unitStatusResponse[unit.unitStatus]
                        ),
                      };
                    }
                    return unit;
                  });
                  return unitGroup;
                }
              );

              return availableUnits;
            } else if (res?.errors?.length > 0) {
              let error = res?.errors[0];
              switch (error.errorType) {
                case ERROR_CODES.INTERNAL_SERVER_ERROR:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_DASHBOARD_DETAILS')
                  );
                default:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_DASHBOARD_DETAILS')
                  );
              }
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }

  /**
   * @description this method calls API to get all this eventsList by site Id
   * @param { string } siteId
   * @returns { eventsList }
   */
  getActiveIssues({
    siteId,
    hideLoader,
    sync = true,
    filterCriteria,
  }: {
    siteId: string;
    hideLoader?: boolean;
    sync?: boolean;
    filterCriteria?: {
      searchEntity?: { deviceName: string };
      pagination?: {
        cursor?: [string];
        size?: number;
      };
    };
  }): Observable<ActiveIssues | undefined> {
    const commonAccessControl: boolean =
      this.stateManagementService.getSharedData()?.selectedSite
        ?.commonAccessControl;

    if (filterCriteria?.pagination?.cursor?.every((value) => value === '')) {
      delete filterCriteria.pagination.cursor;
    }
    return this.http
      .post<any>(
        `${this.url}`,
        {
          query: `
        query getActiveIssuesBySite ($siteId: String!, $filterCriteria: ActiveIssuesFilterCriteria) { getActiveIssuesBySite (siteId: $siteId, filterCriteria: $filterCriteria) { activeIssues { cursor totalCount issues { buildingId buildingName deviceName eventCode eventDescription id floorId  timestamp isoTime unitId unitName accessPointId accessPointName user unitPrefix}}}}        
        `,
          variables: {
            siteId,
            filterCriteria,
          },
        },
        {
          headers: Utility.loaderHeader(hideLoader),
        }
      )
      .pipe(
        map((res) => {
          try {
            if (res?.data && res?.data?.getActiveIssuesBySite?.activeIssues) {
              let ActiveIssueData =
                res?.data?.getActiveIssuesBySite?.activeIssues;

              // hide access point
              if (!commonAccessControl) {
                ActiveIssueData[0].issues = ActiveIssueData[0]?.issues?.filter(
                  (issue: any) =>
                    !('accessPointId' in issue && issue.accessPointId !== null)
                );
              }

              this.stateManagementService.updateActiveIssues(ActiveIssueData);
              return ActiveIssueData;
            } else if (res?.errors?.length > 0) {
              let error = res?.errors[0];
              switch (error.errorType) {
                case ERROR_CODES.INTERNAL_SERVER_ERROR:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_DASHBOARD_DETAILS')
                  );
                default:
                  throw new Error(
                    this.translate.instant('FAILED_TO_GET_DASHBOARD_DETAILS')
                  );
              }
            }
          } catch (error: any) {
            throw new Error(error?.message);
          }
        })
      );
  }

  setTriggerRerender(value: boolean) {
    this.triggerRerender.next(value);
  }
}
